import { useEffect, useState } from 'react'
import { Loader2, Receipt } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { fetchPosReceiptSettings, updatePosReceiptSettings } from '@/lib/api/posReceiptSettings'
import { cn } from '@/lib/utils'

import { SettingsSection } from './SettingsSection'

const inputClass = cn(
  'w-full rounded-xl border border-border/80 bg-card px-4 py-3 text-sm',
  'focus:outline-none focus:ring-2 focus:ring-ring',
)

export function ReceiptSettingsSection({ embedded = false }) {
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [footer, setFooter] = useState('')

  useEffect(() => {
    let active = true
    setLoading(true)

    fetchPosReceiptSettings()
      .then((settings) => {
        if (active) {
          setFooter(settings.receipt_footer ?? '')
        }
      })
      .catch((err) => {
        if (active) {
          setError(err?.response?.data?.message ?? 'Unable to load receipt settings.')
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false)
        }
      })

    return () => {
      active = false
    }
  }, [])

  async function handleSubmit(event) {
    event.preventDefault()
    setSubmitting(true)
    setError('')
    setSuccess('')

    try {
      await updatePosReceiptSettings({ receipt_footer: footer || null })
      setSuccess('Receipt message saved.')
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Unable to save receipt message.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <SettingsSection
      id="receipt"
      icon={Receipt}
      title="Receipt"
      description="Footer message on printed receipts"
      embedded={embedded}
    >
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading…
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="receipt-footer" className="mb-1.5 block text-sm font-medium">
              Footer message
            </label>
            <textarea
              id="receipt-footer"
              rows={3}
              value={footer}
              onChange={(e) => {
                setFooter(e.target.value)
                setSuccess('')
              }}
              className={inputClass}
              placeholder="Thank you for visiting us!"
            />
          </div>
          {error ? (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          ) : null}
          {success ? (
            <p className="text-sm text-primary" role="status">
              {success}
            </p>
          ) : null}
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save receipt message'}
          </Button>
        </form>
      )}
    </SettingsSection>
  )
}
